"use client";

import { uploadWithProgress } from "@/lib/upload-with-progress";

const MAX_WIDTH = 1280;
const JPEG_QUALITY = 0.85;
const DEFAULT_SEEK_SEC = 1;
const CAPTURE_TIMEOUT_MS = 20_000;

export function captureFrame(video: HTMLVideoElement): Promise<Blob> {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) {
    return Promise.reject(
      new Error("Video is not ready yet. Wait for it to load, then try again."),
    );
  }
  const scale = Math.min(1, MAX_WIDTH / vw);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(vw * scale));
  canvas.height = Math.max(1, Math.round(vh * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return Promise.reject(new Error("Could not capture frame."));
  }
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Could not capture frame."));
      },
      "image/jpeg",
      JPEG_QUALITY,
    );
  });
}

function defaultSeekSec(durationSec: number): number {
  if (!Number.isFinite(durationSec) || durationSec > DEFAULT_SEEK_SEC) {
    return DEFAULT_SEEK_SEC;
  }
  return Math.max(0, durationSec / 2);
}

function waitForVideoEvent(
  video: HTMLVideoElement,
  event: string,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    };
    const onTimeout = () => {
      cleanup();
      reject(new Error("Timed out waiting for a video frame."));
    };
    const onEvent = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error("The video failed to load."));
    };
    const cleanup = () => {
      window.clearTimeout(timer);
      video.removeEventListener(event, onEvent);
      video.removeEventListener("error", onError);
      signal?.removeEventListener("abort", onAbort);
    };
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = window.setTimeout(onTimeout, CAPTURE_TIMEOUT_MS);
    video.addEventListener(event, onEvent, { once: true });
    video.addEventListener("error", onError, { once: true });
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export async function captureDefaultPosterFromFile(
  file: File,
  signal?: AbortSignal,
): Promise<File> {
  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.src = url;
  try {
    await waitForVideoEvent(video, "loadedmetadata", signal);
    const seek = defaultSeekSec(video.duration);
    if (seek > 0) {
      const seeked = waitForVideoEvent(video, "seeked", signal);
      video.currentTime = Math.min(seek, Math.max(0, video.duration - 0.05));
      await seeked;
    }
    const blob = await captureFrame(video);
    return new File([blob], "poster.jpg", { type: "image/jpeg" });
  } finally {
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(url);
  }
}

export async function uploadPosterFile(
  file: File,
  signal?: AbortSignal,
): Promise<string> {
  const presignRes = await fetch("/api/uploads/presign-thumbnail", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type || "image/jpeg",
    }),
    signal,
  });
  const data = (await presignRes.json()) as {
    uploadUrl?: string;
    storageKey?: string;
    contentType?: string;
    error?: string;
  };
  if (!presignRes.ok || !data.uploadUrl || !data.storageKey) {
    throw new Error(data.error ?? "Could not start thumbnail upload");
  }
  await uploadWithProgress(
    data.uploadUrl,
    file,
    data.contentType || file.type || "image/jpeg",
    () => {},
    signal,
  );
  return data.storageKey;
}

export async function captureAndUploadDefaultPoster(
  file: File,
  signal?: AbortSignal,
): Promise<string | null> {
  try {
    const poster = await captureDefaultPosterFromFile(file, signal);
    return await uploadPosterFile(poster, signal);
  } catch {
    return null;
  }
}
